import React from 'react'
import { cn } from '@/lib/utils'

type Variant =
    | 'body-xs'
    | 'body-sm'
    | 'body-md'
    | 'title-sm'
    | 'title-md'
    | 'title-lg'
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'inherit'

const variantClasses: Record<Variant, string> = {
    'body-xs': 'text-xs text-muted-foreground',
    'body-sm': 'text-sm text-muted-foreground',
    'body-md': 'text-base',
    'title-sm': 'text-sm font-semibold',
    'title-md': 'text-base font-semibold',
    'title-lg': 'text-lg font-semibold',
    h1: 'scroll-m-20 text-4xl font-extrabold tracking-tight',
    h2: 'scroll-m-20 text-3xl font-semibold tracking-tight',
    h3: 'scroll-m-20 text-2xl font-semibold tracking-tight',
    h4: 'scroll-m-20 text-xl font-semibold tracking-tight',
    inherit: ''
}

const variantTags: Record<Variant, keyof React.JSX.IntrinsicElements> = {
    'body-xs': 'p',
    'body-sm': 'p',
    'body-md': 'p',
    'title-sm': 'p',
    'title-md': 'p',
    'title-lg': 'p',
    h1: 'h1',
    h2: 'h2',
    h3: 'h3',
    h4: 'h4',
    inherit: 'span'
}

interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
    variant?: Variant
    component?: keyof React.JSX.IntrinsicElements
    noWrap?: boolean
    textAlign?: 'left' | 'center' | 'right'
    startDecorator?: React.ReactNode
    endDecorator?: React.ReactNode
}

export function Typography({
    variant = 'body-md',
    component,
    noWrap,
    textAlign,
    className,
    children,
    startDecorator,
    endDecorator,
    ...props
}: TypographyProps) {
    const Tag = (component || variantTags[variant]) as React.ElementType
    const hasDecorators = startDecorator || endDecorator

    return (
        <Tag
            className={cn(
                variantClasses[variant],
                noWrap && 'truncate',
                textAlign === 'left' && 'text-left',
                textAlign === 'center' && 'text-center',
                textAlign === 'right' && 'text-right',
                hasDecorators && 'inline-flex items-center gap-1',
                className
            )}
            {...props}
        >
            {startDecorator}
            {children}
            {endDecorator}
        </Tag>
    )
}
